import React from 'react';
import {
  Alert,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { CommissioningDraft } from './types';

export function DeviceMappingEditor({
  draft,
  onChange,
  readOnly = false,
}: {
  draft: CommissioningDraft;
  onChange: (draft: CommissioningDraft) => void;
  readOnly?: boolean;
}) {
  const editable = draft.status === 'draft' && !readOnly;
  const validUnitKeys = new Set(draft.units.map((unit) => unit.key));
  const floorsByKey = new Map(
    draft.floors.map((floor) => [floor.key, floor.name]),
  );
  const unitNameCounts = new Map<string, number>();
  for (const unit of draft.units) {
    const name = unit.name.trim().toLowerCase();
    unitNameCounts.set(name, (unitNameCounts.get(name) ?? 0) + 1);
  }
  const hasValidDestination = (device: CommissioningDraft['devices'][number]) =>
    !device.ignored && !!device.unitKey && validUnitKeys.has(device.unitKey);
  const needsReview = new Set(draft.needsReview);
  const confirmableHighConfidence = new Set(
    draft.devices
      .filter(
        (device) =>
          hasValidDestination(device) &&
          Number.isFinite(device.confidence) &&
          device.confidence >= 0.9 &&
          needsReview.has(device.discoveredDeviceId),
      )
      .map((device) => device.discoveredDeviceId),
  );
  const confirmReview = (discoveredDeviceId: string) =>
    editable &&
    onChange({
      ...draft,
      needsReview: draft.needsReview.filter(
        (value) => value !== discoveredDeviceId,
      ),
    });
  const updateDevice = (key: string, patch: Record<string, unknown>) => {
    if (!editable) return;
    const reviewedDevice = draft.devices.find((device) => device.key === key);
    const resolvesReview =
      ('unitKey' in patch &&
        typeof patch.unitKey === 'string' &&
        validUnitKeys.has(patch.unitKey)) ||
      patch.ignored === true;
    const requiresReview =
      patch.ignored === false ||
      ('unitKey' in patch && !patch.unitKey && !reviewedDevice?.ignored);
    onChange({
      ...draft,
      devices: draft.devices.map((device) =>
        device.key === key ? { ...device, ...patch } : device,
      ),
      needsReview:
        resolvesReview && reviewedDevice
          ? draft.needsReview.filter(
              (value) => value !== reviewedDevice.discoveredDeviceId,
            )
          : requiresReview && reviewedDevice
            ? [
                ...new Set([
                  ...draft.needsReview,
                  reviewedDevice.discoveredDeviceId,
                ]),
              ]
            : draft.needsReview,
    });
  };
  return (
    <Stack spacing={2}>
      <Button
        disabled={!editable || confirmableHighConfidence.size === 0}
        onClick={() =>
          onChange({
            ...draft,
            needsReview: draft.needsReview.filter(
              (value) => !confirmableHighConfidence.has(value),
            ),
          })
        }
        variant="outlined"
      >
        Accept all high-confidence
      </Button>
      {draft.devices.map((device) => (
        <Stack key={device.key} spacing={1}>
          <Typography variant="subtitle1">{device.originalLabel}</Typography>
          <Typography color="text.secondary" variant="caption">
            Stable discovery identity: {device.discoveredDeviceId}
          </Typography>
          {needsReview.has(device.discoveredDeviceId) && !device.ignored && (
            <Alert severity="warning">
              Confirm this destination or ignore the device before provisioning.
            </Alert>
          )}
          <TextField
            disabled={!editable || !!device.ignored}
            inputProps={{ 'aria-label': `Device name ${device.originalLabel}` }}
            label="Super device name"
            onChange={(event) =>
              updateDevice(device.key, { proposedName: event.target.value })
            }
            size="small"
            value={device.proposedName}
          />
          <TextField
            disabled={!editable || !!device.ignored}
            label="Destination Unit"
            onChange={(event) =>
              updateDevice(device.key, {
                unitKey: event.target.value || undefined,
              })
            }
            select
            size="small"
            value={device.unitKey ?? ''}
          >
            <MenuItem value="">Needs review</MenuItem>
            {draft.units.map((unit) => (
              <MenuItem key={unit.key} value={unit.key}>
                {unit.name}
                {(unitNameCounts.get(unit.name.trim().toLowerCase()) ?? 0) > 1
                  ? ` (${floorsByKey.get(unit.floorKey ?? '') ?? 'No floor'}; ${unit.key})`
                  : ''}
              </MenuItem>
            ))}
          </TextField>
          <Button
            aria-label={`Confirm review ${device.originalLabel}`}
            disabled={
              !editable ||
              !hasValidDestination(device) ||
              !needsReview.has(device.discoveredDeviceId)
            }
            onClick={() => confirmReview(device.discoveredDeviceId)}
          >
            {needsReview.has(device.discoveredDeviceId)
              ? 'Confirm review'
              : 'Reviewed'}
          </Button>
          <FormControlLabel
            control={
              <Checkbox
                disabled={!editable}
                checked={!!device.ignored}
                onChange={(event) =>
                  updateDevice(device.key, { ignored: event.target.checked })
                }
              />
            }
            label={`Ignore ${device.originalLabel}`}
          />
        </Stack>
      ))}
    </Stack>
  );
}
